/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Box,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  ReferenceStoreDto,
  CreateReferenceStoreResponseDtoStatusEnum,
} from '@ink-ai/insight-service-sdk';
import { formatDate } from '@ink-ai/portal/common/utils';
import { PaginatedTable } from '../../../common/components/paginated-table/PaginatedTable';
import { EllipsisCell } from '../../../common/components/paginated-table/EllipsisCell';
import { CopyableId } from '../../../common/components/CopyableId';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import {
  listReferenceStores,
  statusClassMapping,
  statusStringMapping,
} from '@ink-ai/portal/reducers/reference-store';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { DeleteOutlineOutlined, FileDownloadOutlined, DriveFileMoveOutlined, SyncOutlined } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';
import FolderIcon from '@mui/icons-material/Folder';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { app } from '@ink-ai/portal/reducers/app';

// Fallback mapping to ensure bilingual display even if status comes as string
const statusFallbackMapping: Record<string, string> = {
  importing: 'referenceStore.status.importing',
  ready: 'referenceStore.status.ready',
  error: 'referenceStore.status.failed',
  failed: 'referenceStore.status.failed',
};

export const statusIconMapping: Record<
  CreateReferenceStoreResponseDtoStatusEnum,
  React.ReactNode
> = {
  [CreateReferenceStoreResponseDtoStatusEnum.Importing]: (
    <PendingOutlinedIcon />
  ),
  [CreateReferenceStoreResponseDtoStatusEnum.Ready]: (
    <CheckCircleOutlineOutlinedIcon />
  ),
  [CreateReferenceStoreResponseDtoStatusEnum.Error]: (
    <ErrorOutlineOutlinedIcon />
  ),
};

// Explorer mixed item type to support folders and references uniformly
type ExplorerItem = {
  uuid: string;
  name: string;
  type: 'folder' | 'reference';
  desc?: string;
  status?: CreateReferenceStoreResponseDtoStatusEnum;
  createdAt?: string | number;
  updatedAt?: string | number;
  itemCount?: number;
  parentId?: string | null;
  errorMessage?: string;
};

interface ReferenceStoreTableProps {
  items?: ExplorerItem[];
  selectedReferenceStores: string[];
  selectedFolders?: string[];
  searchTerm: string;
  onNavigate?: (referenceStore: ReferenceStoreDto) => void;
  onEnterFolder?: (folderUuid: string, folderName?: string) => void;
  onDeleteFolderClick?: (folderId: string) => void;
  onMoveClick?: (referenceStore?: ReferenceStoreDto) => void;
  onDownloadClick?: (referenceStore: ReferenceStoreDto) => void;
  onDeleteClick?: (referenceStore: ReferenceStoreDto) => void;
  onUpdateClick?: (referenceStore: ReferenceStoreDto) => void;
  page: number;
  rowsPerPage?: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSelectedReferenceStoresChange: (selectedReferenceStores: string[]) => void;
  onSelectedFoldersChange?: (selectedFolders: string[]) => void;
  onTotalItemCountChange?: (total: number) => void;
  allowNonReadySelection?: boolean;
  enableSelection?: boolean;
  showActions?: boolean;
  // Per-row mutability gate (see GlossaryCollectionTable). When false, the row's
  // edit/delete/move actions are hidden; read-only actions (download) remain.
  canMutateRow?: (item: any) => boolean;
  addingFolder?: boolean;
  draftFolderName?: string;
  onDraftFolderNameChange?: (name: string) => void;
  onConfirmAddFolder?: () => void;
  onCancelAddFolder?: () => void;
  // Enable cascading selection: selecting a folder toggles its direct children items
  cascadeSelection?: boolean;
  onReloadExplorer?: () => void;
  totalCount?: number;
}

export const ReferenceStoreTable: React.FC<ReferenceStoreTableProps> = ({
  items,
  selectedReferenceStores,
  selectedFolders = [],
  onSelectedReferenceStoresChange,
  onSelectedFoldersChange,
  searchTerm,
  onNavigate,
  onEnterFolder,
  onDeleteFolderClick,
  onMoveClick,
  onDownloadClick,
  onDeleteClick,
  onUpdateClick,
  page,
  rowsPerPage: propsRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  allowNonReadySelection = true,
  enableSelection = false,
  showActions = false,
  canMutateRow,
  addingFolder = false,
  draftFolderName = '',
  onDraftFolderNameChange,
  onConfirmAddFolder,
  onCancelAddFolder,
  cascadeSelection = false,
  onReloadExplorer,
  totalCount,
}) => {
  const dispatch = useDispatch<any>();
  const referenceStore = useSelector(
    (state: RootState) => state.referenceStore,
  );
  const formatter = new Intl.NumberFormat('en-US');
  const [localRowsPerPage, setLocalRowsPerPage] = useState(10);
  const rowsPerPage = propsRowsPerPage ?? localRowsPerPage;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState<string>('');

  useEffect(() => {
    if (!items) {
      dispatch(listReferenceStores());
    }
  }, [dispatch, items]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const isServerDriven = !!items;
      const currentPageItems = isServerDriven
        ? filteredItems
        : filteredItems.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
          );
      const selectableReferences = currentPageItems.filter((it) => it.uuid !== '__draft__' && it.type === 'reference');
      const selectableFolders = currentPageItems.filter((it) => it.uuid !== '__draft__' && it.type === 'folder');
      const newSelectedReferences = selectableReferences.map((g) => g.uuid);
      const newSelectedFolders = selectableFolders.map((f) => f.uuid);
      onSelectedReferenceStoresChange(newSelectedReferences);
      onSelectedFoldersChange && onSelectedFoldersChange(newSelectedFolders);
    } else {
      onSelectedReferenceStoresChange([]);
      onSelectedFoldersChange && onSelectedFoldersChange([]);
    }
  };

  const handleClick = async (uuid: string) => {
    const item = filteredItems.find((it) => it.uuid === uuid);
    if (!item) return;
    if (item.type === 'folder') {
      const selectedIndex = selectedFolders.indexOf(uuid);
      let newSelected: string[] = [];
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedFolders, uuid);
        // Cascade: when selecting a folder, also select its direct child references
        if (cascadeSelection) {
          const inListChildren = filteredItems.filter((it) => it.parentId === uuid && it.type === 'reference');
          const hiddenChildren: any[] = Array.isArray((item as any)?.children) ? (item as any).children : [];
          const mergedChildren = [...inListChildren, ...hiddenChildren];
          const allowedChildIds = mergedChildren
            .filter((it: any) => allowNonReadySelection || it.status === CreateReferenceStoreResponseDtoStatusEnum.Ready)
            .map((it: any) => it.uuid);
          const merged = Array.from(new Set([...selectedReferenceStores, ...allowedChildIds]));
          onSelectedReferenceStoresChange(merged);
        }
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedFolders.slice(1));
        if (cascadeSelection) {
          const inListChildren = filteredItems.filter((it) => it.parentId === uuid && it.type === 'reference');
          const hiddenChildren: any[] = Array.isArray((item as any)?.children) ? (item as any).children : [];
          const childIds = [...inListChildren, ...hiddenChildren].map((it: any) => it.uuid);
          const pruned = selectedReferenceStores.filter((id) => !childIds.includes(id));
          onSelectedReferenceStoresChange(pruned);
        }
      } else if (selectedIndex === selectedFolders.length - 1) {
        newSelected = newSelected.concat(selectedFolders.slice(0, -1));
        if (cascadeSelection) {
          const inListChildren = filteredItems.filter((it) => it.parentId === uuid && it.type === 'reference');
          const hiddenChildren: any[] = Array.isArray((item as any)?.children) ? (item as any).children : [];
          const childIds = [...inListChildren, ...hiddenChildren].map((it: any) => it.uuid);
          const pruned = selectedReferenceStores.filter((id) => !childIds.includes(id));
          onSelectedReferenceStoresChange(pruned);
        }
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedFolders.slice(0, selectedIndex),
          selectedFolders.slice(selectedIndex + 1),
        );
        if (cascadeSelection) {
          const inListChildren = filteredItems.filter((it) => it.parentId === uuid && it.type === 'reference');
          const hiddenChildren: any[] = Array.isArray((item as any)?.children) ? (item as any).children : [];
          const childIds = [...inListChildren, ...hiddenChildren].map((it: any) => it.uuid);
          const pruned = selectedReferenceStores.filter((id) => !childIds.includes(id));
          onSelectedReferenceStoresChange(pruned);
        }
      }
      onSelectedFoldersChange && onSelectedFoldersChange(newSelected);
      return;
    }
    const referenceStoreItem = referenceStore.referenceStores.find((g) => g.uuid === uuid);
    if (!allowNonReadySelection && referenceStoreItem?.status !== CreateReferenceStoreResponseDtoStatusEnum.Ready) {
      return;
    }
    const selectedIndex = selectedReferenceStores.indexOf(uuid);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedReferenceStores, uuid);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedReferenceStores.slice(1));
    } else if (selectedIndex === selectedReferenceStores.length - 1) {
      newSelected = newSelected.concat(selectedReferenceStores.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedReferenceStores.slice(0, selectedIndex),
        selectedReferenceStores.slice(selectedIndex + 1),
      );
    }
    onSelectedReferenceStoresChange(newSelected);
  };

  const handlePageChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    newPage: number,
  ) => {
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
    } else {
      setLocalRowsPerPage(newRowsPerPage);
    }
    onPageChange(0);
  };

  const baseItems: ExplorerItem[] = useMemo(() => {
    if (items) return items;
    return (referenceStore.referenceStores || []).map((g) => ({
      uuid: g.uuid,
      name: g.name,
      type: 'reference',
      desc: g.desc,
      status: g.status,
      createdAt: g.createdAt,
      updatedAt: g.updatedAt,
      itemCount: (g as any).itemCount ?? (g as any).itemsCount,
      parentId: null,
    }));
  }, [items, referenceStore.referenceStores]);

  const filteredItems = useMemo(
    () => (baseItems || []).filter((g) => (g.name || '').toLowerCase().includes(searchTerm.toLowerCase())),
    [baseItems, searchTerm],
  );

  const displayItems = useMemo(() => {
    if (!addingFolder) return filteredItems;
    const draftRow: ExplorerItem = {
      uuid: '__draft__',
      name: draftFolderName ?? '',
      type: 'folder',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      itemCount: 0,
    };
    return [draftRow, ...filteredItems];
  }, [addingFolder, draftFolderName, filteredItems]);

  // 当处于某个文件夹视图时，如果该文件夹的子项在当前视图中全部取消勾选，则同步取消该父文件夹的勾选
  useEffect(() => {
    if (!enableSelection || !cascadeSelection || !onSelectedFoldersChange) {
      console.log('[Reference] sync-skip', { enableSelection, cascadeSelection, hasHandler: !!onSelectedFoldersChange });
      return;
    }
    // 当前视图中的父文件夹ID集合（基于子项的 parentId）
    const parentIds = Array.from(
      new Set(
        (displayItems || [])
          .map((it) => it.parentId)
          .filter((id): id is string => !!id),
      ),
    );
    if (parentIds.length === 0) {
      console.log('[Reference] sync-noparent', { pageParents: parentIds, displayCount: (displayItems || []).length });
      return;
    }

    const nextSelectedFolders = [...selectedFolders];
    let changed = false;
    console.log('[Reference] sync-start', {
      parentIds,
      selectedFolders,
      selectedReferenceStores,
      displayItemsCount: (displayItems || []).length,
    });
    parentIds.forEach((folderId) => {
      if (nextSelectedFolders.includes(folderId)) {
        const hasSelectedChild = (displayItems || []).some(
          (it) => it.parentId === folderId && it.type === 'reference' && selectedReferenceStores.includes(it.uuid),
        );
        console.log('[Reference] folder-check', { folderId, hasSelectedChild });
        if (!hasSelectedChild) {
          const idx = nextSelectedFolders.indexOf(folderId);
          if (idx > -1) {
            nextSelectedFolders.splice(idx, 1);
            changed = true;
          }
        }
      }
    });

    if (changed) {
      console.log('[Reference] sync-apply', { before: selectedFolders, after: nextSelectedFolders });
      onSelectedFoldersChange(nextSelectedFolders);
    } else {
      console.log('[Reference] sync-nochange');
    }
  }, [displayItems, selectedReferenceStores, selectedFolders, enableSelection, cascadeSelection, onSelectedFoldersChange]);

  // 组件挂载日志，确认补丁已生效
  useEffect(() => {
    console.log('[Reference] component-mounted');
  }, []);

  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: t('referenceStore.table.name'),
        width: 250,
        render: (it: ExplorerItem) => {
          if (it.uuid === '__draft__') {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <FolderIcon fontSize="small" />
                <TextField
                  size="small"
                  value={draftFolderName}
                  onChange={(e) => onDraftFolderNameChange && onDraftFolderNameChange(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  fullWidth
                  autoFocus
                />
                <IconButton
                  size="small"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfirmAddFolder && onConfirmAddFolder();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="confirm-add-folder"
                >
                  <CheckOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelAddFolder && onCancelAddFolder();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="cancel-add-folder"
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          }
          if (it.type === 'folder') {
            if (editingFolderId === it.uuid) {
              return (
                <Box display="flex" alignItems="center" gap={1}>
                  <FolderIcon fontSize="small" />
                  <TextField
                    size="small"
                    value={editingFolderName}
                    onChange={(e) => setEditingFolderName(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    fullWidth
                    autoFocus
                  />
                  <IconButton
                    size="small"
                    color="success"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const name = (editingFolderName || '').trim();
                      if (!name || !editingFolderId) { setEditingFolderId(null); setEditingFolderName(''); return; }
                      try {
                        console.log('Reference: confirm rename', { id: editingFolderId, name });
                        await makeRequest({ method: 'PUT', url: `/api/v1/referenceStore/folders/${editingFolderId}/rename`, data: { name } });
                        console.log('Reference: rename success, trigger onFolderRenamed');
                        onReloadExplorer && onReloadExplorer();
                        setEditingFolderId(null);
                        setEditingFolderName('');
                      } catch (err: any) {
                        const msg = err?.response?.data?.message ?? err?.data?.message ?? err?.message ?? String(err);
                        dispatch(app.actions.setGlobalMessage({ message: msg, status: 'error' }));
                      }
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label="confirm-rename-folder"
                  >
                    <CheckOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => { e.stopPropagation(); setEditingFolderId(null); setEditingFolderName(''); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label="cancel-rename-folder"
                  >
                    <CloseOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            }
            return (
              <EllipsisCell
                maxLines={3}
                content={
                  <Link
                    component="span"
                    underline="hover"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEnterFolder && onEnterFolder(it.uuid, it.name);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ cursor: 'pointer' }}
                  >
                    <FolderIcon fontSize='small' />
                    {it.name}
                  </Link>
                }
                tooltip={it.name}
              />
            );
          }
          // Folder rows already returned above; here `it` is always a reference.
          const showId = it.uuid !== '__draft__';
          return (
            <Box>
              <EllipsisCell
                maxLines={3}
                content={
                  <Link
                    component="span"
                    underline="hover"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate && onNavigate({
                        uuid: it.uuid,
                        name: it.name,
                        desc: it.desc || '',
                        status: it.status as any,
                        createdAt: String(it.createdAt || ''),
                        updatedAt: String(it.updatedAt || ''),
                        itemCount: (it.itemCount || 0) as any,
                      } as ReferenceStoreDto);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ cursor: 'pointer' }}
                  >
                    <DescriptionOutlinedIcon fontSize='small' />
                    {it.name}
                  </Link>
                }
                tooltip={it.name}
              />
              {showId && <CopyableId id={it.uuid} />}
            </Box>
          );
        },
      },
      {
        id: 'description',
        label: t('referenceStore.table.description'),
        width: 200,
        render: (it: ExplorerItem) => (
          <EllipsisCell content={it.uuid === '__draft__' || it.type === 'folder' ? '-' : it.desc || '-'} maxLines={3} />
        ),
        hidden: isSmallScreen,
      },
      // {
      //   id: 'languages',
      //   label: t('referenceStore.table.languages'),
      //   width: 150,
      //   render: (referenceStore: ReferenceStoreDto) => (
      //     <EllipsisCell
      //       content={
      //         referenceStore.uuid === '__draft__' 
      //         ? '-' 
      //         : referenceStore.status ===
      //           CreateReferenceStoreResponseDtoStatusEnum.Ready
      //             ? referenceStore.languages.join(', ')
      //             : t('referenceStore.table.na')
      //       }
      //     />
      //   ),
      // },
      {
        id: 'terms',
        label: t('referenceStore.table.terms'),
        width: 100,
        render: (it: ExplorerItem) =>
          it.uuid === '__draft__' || it.type === 'folder'
            ? '-' 
            : it.status === CreateReferenceStoreResponseDtoStatusEnum.Ready
                ? formatter.format(it.itemCount || 0)
                : t('referenceStore.table.na'),
        hidden: isSmallScreen,
      },
      {
        id: 'updatedAt',
        label: t('referenceStore.table.updatedAt'),
        render: (it: ExplorerItem) => {
          if (it.uuid === '__draft__' || it.type === 'folder') return '-';
          const ts = typeof it.updatedAt === 'string' ? new Date(it.updatedAt).getTime() : (it.updatedAt as number | undefined);
          return formatDate(ts);
        },
        width: 180,
        hidden: isMediumScreen,
      },
      {
        id: 'status',
        label: t('referenceStore.table.status'),
        width: 100,
        render: (it: ExplorerItem) => {
          if (it.uuid === '__draft__' || it.type === 'folder') {
            return <Typography variant="body2">-</Typography>;
          }
          
          const rawStatus = String(it.status || '').toLowerCase();
          const statusKey = statusStringMapping[it.status as any] || statusFallbackMapping[rawStatus] || 'referenceStore.table.na';

          const content = (
            <Typography
              variant="body2"
              className={statusClassMapping[it.status as any]}
            >
              {statusIconMapping[it.status as any]}
              <span className="align-middle ml-1">
                {t(statusKey)}
              </span>
            </Typography>
          );

          // 预留错误提示展示：当存在 errorMessage 时显示 Tooltip
          if (it.errorMessage) {
            return (
              <Tooltip title={it.errorMessage} placement="top" arrow>
                {content}
              </Tooltip>
            );
          }

          return content;
        },
        hidden: isSmallScreen,
      },
      {
        id: 'action',
        label: t('table.actions'),
        width: 200,
        render: (it: ExplorerItem) => {
          if (it.uuid === '__draft__') return '-';
          // Read-only rows (default-protected for non-super-admins) keep only
          // non-mutating actions.
          const mutable = canMutateRow ? canMutateRow(it) : true;
          return it.type === 'folder' ? (
            !mutable ? '-' : (
            <>
              <Tooltip title={t('table.tooltip.rename', { defaultValue: '重命名' })} placement="top">
                <IconButton
                  color="primary"
                  onClick={(e) => { e.stopPropagation(); setEditingFolderId(it.uuid); setEditingFolderName(it.name || ''); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="rename-folder"
                >
                  <EditOutlinedIcon/>
                </IconButton>
              </Tooltip>
              <Tooltip title={t('table.tooltip.delete')} placement="top">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFolderClick && onDeleteFolderClick(it.uuid);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="delete-folder"
                >
                  <DeleteOutlineOutlined/>
                </IconButton>
              </Tooltip>
            </>
            )
          ) : (
            <>
              <Tooltip title={t('table.tooltip.download')} placement="top">
                <IconButton
                  color="primary"
                  disabled={it.status === CreateReferenceStoreResponseDtoStatusEnum.Importing}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadClick && onDownloadClick({
                      uuid: it.uuid,
                      name: it.name,
                      desc: it.desc || '',
                      status: it.status as any,
                      createdAt: String(it.createdAt || ''),
                      updatedAt: String(it.updatedAt || ''),
                      itemCount: (it.itemCount || 0) as any,
                    } as ReferenceStoreDto);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="download-reference-store"
                >
                  <FileDownloadOutlined/>
                </IconButton>
              </Tooltip>
              {mutable && (
              <Tooltip title={t('table.tooltip.move')} placement="top">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveClick && onMoveClick({
                      uuid: it.uuid,
                      name: it.name,
                      desc: it.desc || '',
                      status: it.status as any,
                      createdAt: String(it.createdAt || ''),
                      updatedAt: String(it.updatedAt || ''),
                      itemCount: (it.itemCount || 0) as any,
                    } as ReferenceStoreDto);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="move-reference-store"
                >
                  <DriveFileMoveOutlined/>
                </IconButton>
              </Tooltip>
              )}
              {mutable && (
              <Tooltip title={t('table.tooltip.update')} placement="top">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdateClick && onUpdateClick({
                      uuid: it.uuid,
                      name: it.name,
                      desc: it.desc || '',
                      status: it.status as any,
                      createdAt: String(it.createdAt || ''),
                      updatedAt: String(it.updatedAt || ''),
                      itemCount: (it.itemCount || 0) as any,
                    } as ReferenceStoreDto);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  disabled={it.status === CreateReferenceStoreResponseDtoStatusEnum.Importing}
                  aria-label="update-reference-store"
                >
                  <SyncOutlined/>
                </IconButton>
              </Tooltip>
              )}
              {mutable && (
              <Tooltip title={t('table.tooltip.delete')} placement="top">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteClick && onDeleteClick({
                      uuid: it.uuid,
                      name: it.name,
                      desc: it.desc || '',
                      status: it.status as any,
                      createdAt: String(it.createdAt || ''),
                      updatedAt: String(it.updatedAt || ''),
                      itemCount: (it.itemCount || 0) as any,
                    } as ReferenceStoreDto);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="delete-reference-store"
                >
                  <DeleteOutlineOutlined/>
                </IconButton>
              </Tooltip>
              )}
            </>
          );
        },
        hidden: !showActions,
      }
    ],
    [
      isSmallScreen,
      isMediumScreen,
      onNavigate,
      onEnterFolder,
      onMoveClick,
      onDownloadClick,
      onDeleteClick,
      onUpdateClick,
      showActions,
      canMutateRow,
      editingFolderId,
      editingFolderName,
    ],
  );

  const visibleColumns = columns.filter((column) => !column.hidden);

  return (
    <PaginatedTable
      dummyPagination={!items}
      totalCount={items ? totalCount : undefined}
      columns={visibleColumns}
      data={displayItems}
      selectedItems={enableSelection ? [...selectedReferenceStores, ...selectedFolders] : undefined}
      page={page}
      rowsPerPage={rowsPerPage}
      loading={referenceStore.loading}
      // 使用唯一 uuid 作为每一行的 key，避免文件夹行共享同一 key 导致渲染异常
      getItemId={(it: ExplorerItem) => it.uuid}
      onSelectAllClick={enableSelection ? handleSelectAllClick : undefined}
      onItemClick={enableSelection ? handleClick : undefined}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );
};
